import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { captureEvent } from "@/lib/analyticsQueue";
import { toast } from "@/lib/toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCredits } from "@/contexts/CreditsContext";
import { useModels } from "@/hooks/useModels";
import { useCreditCosts } from "@/hooks/useCreditCosts";
import { useGeneratedImages, type GeneratedImage } from "@/hooks/useGeneratedImages";
import { usePersistentPendingBoost } from "@/hooks/usePersistentPendingBoost";
import { useImageDelete } from "@/hooks/useImageDelete";
import { useLightboxNavigation } from "@/hooks/useLightboxNavigation";
import {
  clearFlowState,
  useFlowPersistence,
} from "@/hooks/useFlowPersistence";
import { FLOW_KEYS } from "@/config/flowKeys";
import { apiPost } from "@/lib/api/apiClient";
import { uploadImagesToR2 } from "@/lib/api/uploadImage";
import { useImageUpload } from "@/hooks/useImageUpload";
import { downloadMedia, shareMedia } from "@/lib/mediaUtils";
import { ANALYTICS_EVENTS, FLOW_IDS } from "@shared/schemas/analytics";
import { captureGenerationError } from "@/lib/sentry";
import {
  ASPECT_RATIOS,
  RESOLUTIONS,
} from "@/components/flowly/AspectRatioPicker/constants";
import {
  PRODUCT_CREATIVE_DEFAULT_IMAGES,
  PRODUCT_CREATIVE_MAX_UPLOADS,
} from "@/config/productCreative";
import {
  PRODUCT_CREATIVE_CONFIG_ANCHOR_ID,
  PRODUCT_CREATIVE_PIPELINE,
} from "./constants";
import type { CreativeMode } from "@/components/flowly/ProductCreativeStylePicker/ProductCreativeStylePicker";
import type {
  GenerateProductCreativeRequest,
  GenerateProductCreativeResponse,
  ImageResolution,
} from "@shared/schemas/api";
import type { UploadedSource } from "./types";

const PRODUCT_CREATIVE_MODEL_ID = "nano-banana-2";
const DEFAULT_ASPECT_RATIO = "9:16";
const DEFAULT_RESOLUTION = "1k";

let uploadIdCounter = 0;
const nextUploadId = () => `pc-upload-${Date.now()}-${++uploadIdCounter}`;

export function useProductCreative() {
  const { t } = useLanguage();
  const { normalizeFilesOrWarn } = useImageUpload();
  const { refreshCredits, handleCreditError, deductCredits } = useCredits();
  const { getImageGenerateCost } = useCreditCosts();
  const { models } = useModels("image");
  const {
    images: dbImages,
    pendingImages: dbPendingImages,
    completingIds: imageCompletingIds,
    failedImages,
    loading: imagesLoading,
  } = useGeneratedImages(undefined, PRODUCT_CREATIVE_PIPELINE);
  const { pendingCount, increment: incrementPending, decrement: decrementPending } =
    usePersistentPendingBoost({
      pipeline: PRODUCT_CREATIVE_PIPELINE,
      dbPendingCount: dbPendingImages.length,
      isDbLoading: imagesLoading,
    });
  const { deleteFailedImages, deleteImageByUrl } = useImageDelete();
  const [selectedMedia, setSelectedMedia] = useState<GeneratedImage | null>(null);

  const [uploads, setUploads] = useState<UploadedSource[]>([]);
  const [imageCount, setImageCount] = useState<number>(PRODUCT_CREATIVE_DEFAULT_IMAGES);
  const [aspectRatio, setAspectRatio] = useState<string>(DEFAULT_ASPECT_RATIO);
  const [resolution, setResolution] = useState<string>(DEFAULT_RESOLUTION);
  const [isUploading, setIsUploading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [results, setResults] = useState<string[]>([]);
  // Snapshot of the last finished generation (source + result URLs) so the
  // hero keeps showing the user's before/after after we reset the upload form,
  // instead of snapping back to the default example images. In-memory only —
  // cleared on a new upload, gone on refresh (by design).
  const [lastShowcase, setLastShowcase] = useState<{ before: string; after: string } | null>(null);
  const [creativeMode, setCreativeMode] = useState<CreativeMode | null>(null);
  const [customDirection, setCustomDirection] = useState<string>("");
  const firstUploadTipShownRef = useRef(false);

  // --- Analytics: tool page view ---
  useEffect(() => {
    captureEvent(ANALYTICS_EVENTS.TOOL_PAGE_VIEW, { flow: FLOW_IDS.PRODUCT_CREATIVE });
  }, []);

  const productCreativeModel = useMemo(
    () => models.find((m) => m.id === PRODUCT_CREATIVE_MODEL_ID),
    [models],
  );
  const availableAspectRatios = useMemo(() => {
    const supported = productCreativeModel?.capabilities.supportedAspectRatios;
    if (!supported) return ASPECT_RATIOS;
    return ASPECT_RATIOS.filter((r) => supported.includes(r.id));
  }, [productCreativeModel]);
  const availableResolutions = useMemo(() => {
    const supported = productCreativeModel?.capabilities.supportedResolutions;
    if (!supported) return RESOLUTIONS;
    return RESOLUTIONS.filter((r) => supported.includes(r.id));
  }, [productCreativeModel]);

  /** Add files locally — instant blob preview, NO R2 upload yet. We defer
   *  the R2 push until `generate()` runs (past the anonymous gate), so an
   *  anonymous visitor can configure a full creative without burning our
   *  bandwidth on bytes they can't pay for. */
  const handleAddFiles = useCallback(
    async (rawFiles: File[]) => {
      if (rawFiles.length === 0) return;
      const remainingSlots = PRODUCT_CREATIVE_MAX_UPLOADS - uploads.length;
      const rawAccepted = rawFiles.slice(0, remainingSlots);
      if (rawAccepted.length === 0) return;

      const accepted = await normalizeFilesOrWarn(rawAccepted);
      if (!accepted) return;

      captureEvent(ANALYTICS_EVENTS.IMAGE_UPLOAD, {
        flow: FLOW_IDS.PRODUCT_CREATIVE,
        quantity: accepted.length,
      });

      const wasEmpty = uploads.length === 0;
      const newSources: UploadedSource[] = accepted.map((file) => ({
        id: nextUploadId(),
        publicUrl: "",
        previewUrl: URL.createObjectURL(file),
        file,
      }));
      setUploads((prev) => [...prev, ...newSources]);
      setResults([]);
      setLastShowcase(null);
      if (wasEmpty) {
        requestAnimationFrame(() => {
          document
            .getElementById(PRODUCT_CREATIVE_CONFIG_ANCHOR_ID)
            ?.scrollIntoView({ behavior: "smooth", block: "center" });
        });
        if (!firstUploadTipShownRef.current) {
          firstUploadTipShownRef.current = true;
          toast.info(t.productCreative.firstUploadTipTitle, {
            description: t.productCreative.firstUploadTipDesc,
            duration: 8000,
            position: "top-center",
            closeButton: true,
          });
        }
      }
    },
    [uploads.length, t, normalizeFilesOrWarn],
  );

  const handleRemoveUpload = useCallback((id: string) => {
    setUploads((prev) => {
      const target = prev.find((u) => u.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((u) => u.id !== id);
    });
    setResults([]);
  }, []);

  const handleCreativeModeChange = useCallback((next: CreativeMode) => {
    setCreativeMode(next);
  }, []);

  const handleCustomDirectionChange = useCallback((value: string) => {
    setCustomDirection(value);
  }, []);

  const handleClearFailed = useCallback(() => {
    if (failedImages.length > 0) {
      void deleteFailedImages(failedImages.map((f) => f.id));
    }
  }, [failedImages, deleteFailedImages]);

  // --- Lightbox callbacks ---

  const handleOpenLightbox = useCallback((img: GeneratedImage) => {
    setSelectedMedia(img);
  }, []);

  const handleCloseLightbox = useCallback(() => {
    setSelectedMedia(null);
  }, []);

  const handleDownload = useCallback(async () => {
    if (!selectedMedia?.image_url) return;
    await downloadMedia(selectedMedia.image_url, "product-creative", "png");
  }, [selectedMedia]);

  const handleShare = useCallback(async () => {
    if (!selectedMedia?.id) return;
    await shareMedia({ id: selectedMedia.id, type: "image" });
  }, [selectedMedia]);

  const handleDelete = useCallback(async () => {
    if (!selectedMedia?.image_url) return;
    const url = selectedMedia.image_url;
    setSelectedMedia(null);
    await deleteImageByUrl(url);
  }, [selectedMedia, deleteImageByUrl]);

  /** Navigation across the persisted gallery (cycles through dbImages). */
  const { currentMediaIndex, canNavigate, goToNextItem, goToPrevItem } =
    useLightboxNavigation(dbImages, selectedMedia, setSelectedMedia);

  const styleStepReady =
    creativeMode === "auto" ||
    (creativeMode === "manual" && customDirection.trim().length > 0);
  const canGenerate =
    uploads.length > 0 && styleStepReady && !isGenerating && !isUploading;

  const generate = useCallback(async () => {
    if (!canGenerate) return;

    // Local credit pre-check: open the upgrade modal instantly on click instead
    // of waiting for the server 402 (mirrors useImageGenerator). Server remains
    // the authoritative deduction; handleCreditError in catch is the backstop.
    const totalCost = getImageGenerateCost(resolution, PRODUCT_CREATIVE_MODEL_ID) * imageCount;
    if (!deductCredits(totalCost)) return;

    setIsGenerating(true);
    setResults([]);
    // Optimistic: paint the pending cards now, before R2 upload / network.
    // The DB rows + Realtime update take over a beat later and the boost
    // auto-zeroes (usePersistentPendingBoost). Survives page refresh.
    incrementPending(imageCount);
    try {
      // Lazy R2 upload: only push the source images that are still local
      // (uploads without a publicUrl). Re-renders of the same set of files
      // skip the upload entirely.
      const needUpload = uploads.filter((u) => !u.publicUrl && u.file);
      if (needUpload.length > 0) {
        setIsUploading(true);
        try {
          const uploaded = await uploadImagesToR2(needUpload.map((u) => u.file!));
          setUploads((prev) =>
            prev.map((u) => {
              const idx = needUpload.findIndex((n) => n.id === u.id);
              return idx >= 0 ? { ...u, publicUrl: uploaded[idx].publicUrl } : u;
            }),
          );
          for (let i = 0; i < needUpload.length; i++) {
            needUpload[i].publicUrl = uploaded[i].publicUrl;
          }
        } finally {
          setIsUploading(false);
        }
      }

      const body: GenerateProductCreativeRequest = {
        sourceImageUrls: uploads.map((u) => u.publicUrl),
        imageCount,
        aspectRatio,
        // Picker only yields 1k/2k/4k; the server Zod enum is the authoritative
        // guard that keeps the charged tier == the generated tier.
        resolution: resolution as ImageResolution,
        ...(creativeMode === "manual" && customDirection.trim().length > 0
          ? { customDirection: customDirection.trim() }
          : {}),
      };
      const response = await apiPost<GenerateProductCreativeResponse>(
        "/api/generate-product-creative",
        body,
      );
      setResults(response.images);
      // Pin the before/after to this finished run BEFORE we wipe the upload
      // form, so the hero keeps showing the user's source + result (both are
      // already-validated R2/result URLs — no re-upload, no validation bypass).
      if (uploads[0]?.publicUrl && response.images[0]) {
        setLastShowcase({ before: uploads[0].publicUrl, after: response.images[0] });
      }
      // Render landed — wipe the persisted draft AND reset inputs to defaults
      // (you don't regenerate from the same photo). Results stay visible via
      // the DB-backed gallery, untouched here.
      clearFlowState(FLOW_KEYS.PRODUCT_CREATIVE);
      uploads.forEach((u) => URL.revokeObjectURL(u.previewUrl));
      setUploads([]);
      setImageCount(PRODUCT_CREATIVE_DEFAULT_IMAGES);
      setAspectRatio(DEFAULT_ASPECT_RATIO);
      setResolution(DEFAULT_RESOLUTION);
      setCreativeMode(null);
      setCustomDirection("");
    } catch (err) {
      // Failed before DB rows exist — remove the optimistic cards.
      decrementPending(imageCount);
      console.error("Error generating product creative:", err);
      captureGenerationError(err, {
        flow: FLOW_IDS.PRODUCT_CREATIVE,
        action: "generate",
        model: PRODUCT_CREATIVE_MODEL_ID,
        endpoint: "/api/generate-product-creative",
      });
      // Parity with old flows: insufficient credits → pricing modal;
      // refund-on-failure reflected via refreshCredits() in `finally`.
      if (!handleCreditError(err)) {
        toast.error(t.productCreative.generationFailed, {
          description:
            err instanceof Error ? err.message : t.productCreative.generationFailedDesc,
        });
      }
    } finally {
      setIsGenerating(false);
      await refreshCredits();
    }
  }, [canGenerate, uploads, imageCount, aspectRatio, resolution, creativeMode, customDirection, refreshCredits, handleCreditError, deductCredits, getImageGenerateCost, incrementPending, decrementPending, t]);
  // Auth gate lives inside GenerateButton, so this is just a rename.
  const handleGenerate = generate;

  // Persist config AND the uploaded images across the auth/credit wall so a
  // signup / token purchase doesn't wipe the user's work. `uploads` is an
  // image key → its File bytes go to IndexedDB; the rest is JSON.
  useFlowPersistence({
    key: FLOW_KEYS.PRODUCT_CREATIVE,
    state: { imageCount, aspectRatio, resolution, creativeMode, customDirection, uploads },
    imageKeys: ["uploads"],
    onRestore: (s) => {
      if (typeof s.imageCount === "number") setImageCount(s.imageCount);
      if (typeof s.aspectRatio === "string") setAspectRatio(s.aspectRatio);
      if (typeof s.resolution === "string") setResolution(s.resolution);
      if (s.creativeMode === "auto" || s.creativeMode === "manual") setCreativeMode(s.creativeMode);
      if (typeof s.customDirection === "string") setCustomDirection(s.customDirection);
      if (Array.isArray(s.uploads)) setUploads(s.uploads as UploadedSource[]);
    },
  });

  return {
    // State
    uploads,
    imageCount,
    aspectRatio,
    resolution,
    isUploading,
    isGenerating,
    results,
    lastShowcase,
    creativeMode,
    customDirection,
    availableAspectRatios,
    availableResolutions,
    models,

    // Persistent gallery (from DB via useGeneratedImages)
    dbImages,
    dbPendingImages,
    // max(optimistic boost, DB pending) — drives instant placeholder cards
    pendingCount,
    imageCompletingIds,
    failedImages,
    selectedMedia,

    // Handlers
    handleAddFiles,
    handleRemoveUpload,
    handleCreativeModeChange,
    handleCustomDirectionChange,
    setImageCount,
    setAspectRatio,
    setResolution,
    handleGenerate,
    handleClearFailed,
    handleOpenLightbox,
    handleCloseLightbox,
    handleDownload,
    handleShare,
    handleDelete,

    // Lightbox navigation
    currentMediaIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,

    // Derived
    canGenerate,

    // Constants
    productCreativeModelId: PRODUCT_CREATIVE_MODEL_ID,

    // i18n
    t,
  };
}
